import { Cookies } from 'react-cookie';
import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { isPartialAuthActive } from '../auth/partialAuthStorage';
import { silentlyCheckKeycloakSessionAndRedirectToAppropriatePage } from '../auth/silentSessionExpiredHandler';
import type {
  AuthResponse,
  ForgotPasswordResponse,
  RequestOtpResponse,
  ResetPasswordResponse,
  UserInfo,
  ValidateForgotPasswordLinkResponse,
  VerifyOtpResponse,
} from '../types/auth';
import type { GetSharedData, GetSharedRedirectResponse } from '../types/common';
import {
  clearAllCookies,
  getAccessToken,
  getLoginUserId,
  getRefreshToken,
} from '../util';
import { COMPARE_TOUR_SEEN_STORAGE_PREFIX } from '../util/compareTourStorage';
import { apiURL, isLoggedKey, loginWithKeycloak } from '../util/constants';
import { isRememberMeEnabled } from '../util/decodeJWT';
import { logger } from '../util/logger';
import { redirectToKeyCloakLoginWithError } from '../util/redirectToKeyCloakLoginWithError';
import { classifyAxiosError } from './errorHandling/errorClassification.util';
import { HttpClient } from './axiosConfig';
import {
  attachErrorLogPayload,
  buildApiErrorLogDetails,
  buildBaseErrorLogPayload,
  getAttachedErrorLogPayload,
  logError,
  logGlobalError,
} from './errorLogger';
import { refreshAccessTokenSafely, refreshKeycloakTokenSafely } from './tokenRefresh';

interface ResetPasswordPayload {
  email: string;
  password: string;
}
interface AuthViewDocumentPayload {
  filename: string;
  path?: string;
  renStyleID?: number;
  erenStyleId?: string;
  type?: string;
  compare?: number;
  prodID?: number;
  location?: string;
  isMtob?: number;
  json?: boolean;
}
const useKeycloak = loginWithKeycloak === 'true';
const currentPath = `${globalThis.location.origin}${globalThis.location.pathname}`;

const getRoutePathHeader = (): Record<string, string> => ({
  'X-Current-Path': currentPath,
  'X-Request-ID': crypto.randomUUID(),
});
class AuthService {
  private readonly keycloakAuthority = process.env.REACT_APP_KEYCLOAK_AUTHORITY;
  private readonly cookies = new Cookies();
  private readonly client_id = process.env.REACT_APP_CLIENT_ID;
  private readonly viewer_client_id = process.env.REACT_APP_SECOND_CLIENT_ID;

  private async logAuthApiError(
    error: unknown,
    context: string,
    options?: { awaitCompletion?: boolean }
  ): Promise<void> {
    const axiosError = error as AxiosError;
    if (axiosError?.response?.status === 401) {
      return;
    }

    // Already logged by HttpClient's interceptor (with its X-Request-ID) — don't log twice.
    if (getAttachedErrorLogPayload(error)) {
      return;
    }

    // These calls use raw axios (not the intercepted HttpClient), so nothing else logs
    // them. Log with the same `api` details the interceptor records — URL, method,
    // status, response body — instead of a bare "Request failed with status code 400".
    if (axios.isAxiosError(error)) {
      const classification = classifyAxiosError(error);
      const requestId: unknown = error.config?.headers?.['X-Request-ID'];
      const apiPayload = {
        ...buildBaseErrorLogPayload(
          'api',
          classification.severity,
          `[AUTH_SERVICE][${context}] ${error.message}`,
          error.stack
        ),
        ...(typeof requestId === 'string' && requestId ? { correlationId: requestId } : {}),
        api: buildApiErrorLogDetails(error, classification, 0, 0, true),
      };
      // Lets a caller that reports the same failure again (e.g. Login's OIDC callback
      // catch) see it was already logged and reuse this entry instead of adding another.
      attachErrorLogPayload(error, apiPayload);
      if (options?.awaitCompletion) {
        await logError(apiPayload);
        return;
      }
      void logError(apiPayload);
      return;
    }

    const errorToLog = error instanceof Error
      ? error
      : new Error(`[AUTH_SERVICE][${context}] ${String(error)}`);
    if (options?.awaitCompletion) {
      await logGlobalError(errorToLog);
      return;
    }
    void logGlobalError(errorToLog);
  }

  private async handleOtpFinalUnauthorized(): Promise<void> {
    if (useKeycloak) {
      await redirectToKeyCloakLoginWithError('INVALID_LOGIN');
    }
  }

  private async executeWithKeycloakRefresh<T>(
    apiCall: (accessToken: string | null) => Promise<AxiosResponse<T>>,
    options?: {
      onFinalUnauthorized?: () => Promise<void>;
      refreshMode?: 'beforeAcceptCondition' | 'afterAcceptCondition';
    }
  ): Promise<T> {
    const currentAccessToken = getAccessToken();
    const refreshMode = options?.refreshMode ?? 'beforeAcceptCondition';

    try {
      const initialResponse = await apiCall(currentAccessToken);
      return initialResponse.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;

      if (status !== 401) {
        logger.error('Auth request error:', error);
        await this.logAuthApiError(error, 'EXECUTE_WITH_KEYCLOAK_REFRESH', { awaitCompletion: true });
        throw error;
      }

      let newAccessToken: string | null = null;

      try {
        if (refreshMode === 'beforeAcceptCondition') {
          const refreshToken = getRefreshToken();
          const keycloakClientKey = localStorage.getItem('keycloak_login_client') ?? 'main';
          const clientId =
            keycloakClientKey === 'viewer' ? this.viewer_client_id : this.client_id;

          if (!refreshToken || !clientId || !this.keycloakAuthority) {
            await this.logAuthApiError(error, 'MISSING_REFRESH_CONTEXT', { awaitCompletion: true });
            await this.performLogoutOnUnauthorized();
            throw error;
          }

          const accessToken = await refreshKeycloakTokenSafely();
          newAccessToken = accessToken || null;
        } else {
          newAccessToken = await refreshAccessTokenSafely();
        }
      } catch (refreshError) {
        await this.logAuthApiError(refreshError, 'REFRESH_TOKEN', { awaitCompletion: true });
        const refreshAxiosError = refreshError as AxiosError;
        const refreshStatus = refreshAxiosError.response?.status;
        const refreshErrorPayload = refreshAxiosError.response?.data as {
          error?: string;
          error_description?: string;
        } | undefined;
        const isInvalidGrant =
          refreshStatus === 400 &&
          (refreshErrorPayload?.error === 'invalid_grant' ||
            refreshErrorPayload?.error_description?.toLowerCase().includes('token is not active'));
        const isRefreshUnauthorized = refreshStatus === 401 || isInvalidGrant;

        if (isRefreshUnauthorized && useKeycloak) {
          const silentlyRecovered = await silentlyCheckKeycloakSessionAndRedirectToAppropriatePage();

          if (silentlyRecovered) {
            try {
              const retryAfterSilentResponse = await apiCall(getAccessToken());
              return retryAfterSilentResponse.data;
            } catch (retryAfterSilentError) {
              const retryAfterSilentAxiosError = retryAfterSilentError as AxiosError;
              const isUnauthorizedAfterSilentRetry = retryAfterSilentAxiosError.response?.status === 401;
              await this.logAuthApiError(retryAfterSilentError, 'RETRY_AFTER_SILENT_SSO', {
                awaitCompletion: isUnauthorizedAfterSilentRetry,
              });
              if (isUnauthorizedAfterSilentRetry) {
                const handler =
                  options?.onFinalUnauthorized ?? this.performLogoutOnUnauthorized.bind(this);
                await handler();
              }
              throw retryAfterSilentError;
            }
          }

          throw refreshError;
        }

        await this.performLogoutOnUnauthorized();
        throw refreshError;
      }

      // Retry original request outside of the refresh try/catch so that
      // non-401 errors from the retry do NOT get treated as refresh failures.
      try {
        const retryResponse = await apiCall(newAccessToken);
        return retryResponse.data;
      } catch (retryError) {
        const retryAxiosError = retryError as AxiosError;
        const isUnauthorizedAfterRetry = retryAxiosError.response?.status === 401;
        await this.logAuthApiError(retryError, 'RETRY_API_CALL', {
          awaitCompletion: isUnauthorizedAfterRetry,
        });
        if (retryAxiosError.response?.status === 401) {
          const handler =
            options?.onFinalUnauthorized ?? this.performLogoutOnUnauthorized.bind(this);
          await handler();
        }
        // For all statuses (including non-401), just rethrow so caller can handle.
        throw retryError;
      }
    }
  }

  private async performLogoutOnUnauthorized(): Promise<void> {

    if (useKeycloak) {
      if (isRememberMeEnabled(getAccessToken())) {
        await silentlyCheckKeycloakSessionAndRedirectToAppropriatePage();
      } else {
        redirectToKeyCloakLoginWithError('SESSION_EXPIRED');
      }
    } else {
      await this.logout(true);
      this.customLogout();
    }
  }

  public async login(
    username: string,
    password: string
  ): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await axios.post(
        `${this.keycloakAuthority}/protocol/openid-connect/token`,
        new URLSearchParams({
          username,
          password,
          grant_type: 'authorization_code',
          scope: 'openid',
          client_id: this.client_id as string,
        }),
        {
          headers: {
            ...getRoutePathHeader(),
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      return response.data;
    } catch (error) {
      logger.error('Login error:', error);
      void this.logAuthApiError(error, 'LOGIN');
      throw error;
    }
  }

  public async viewerLogin(
    username: string,
    password: string
  ): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await axios.post(
        `${this.keycloakAuthority}/protocol/openid-connect/token`,
        new URLSearchParams({
          username,
          password,
          grant_type: 'authorization_code',
          scope: 'openid',
          client_id: this.viewer_client_id as string,
        }),
        {
          headers: {
            ...getRoutePathHeader(),
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      return response.data;
    } catch (error) {
      logger.error('Login error:', error);
      void this.logAuthApiError(error, 'VIEWER_LOGIN');
      throw error;
    }
  }

  public async requestOtp(): Promise<RequestOtpResponse> {
    return this.executeWithKeycloakRefresh<RequestOtpResponse>(
      (accessToken) =>
        axios.post(
          `${apiURL}/auth/login/otp`,
          {},
          {
            headers: {
              ...getRoutePathHeader(),
              Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            },
            withCredentials: true,
          }
        ),
      { onFinalUnauthorized: this.handleOtpFinalUnauthorized.bind(this) }
    );
  }

  public async resendOtp(standardLoginOtpId: string): Promise<RequestOtpResponse> {
    const loginUserId = getLoginUserId();

    return this.executeWithKeycloakRefresh<RequestOtpResponse>((accessToken) =>
      axios.post(
        `${apiURL}/auth/login/otp/resend?standardLoginOtpId=${standardLoginOtpId}`,
        {},
        {
          headers: {
            ...getRoutePathHeader(),
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            loginUserId: loginUserId ?? undefined,
          },
          withCredentials: true,
        }
      )
    );
  }

  public async storeCookies(
    standardLoginOtpId?: string,
    otp?: string
  ): Promise<VerifyOtpResponse> {
    const loginUserId = getLoginUserId();
    //Creates an empty collection for query parameters.
    const parameters = new URLSearchParams();
    if (standardLoginOtpId != null && standardLoginOtpId !== '') {
      parameters.set('standardLoginOtpId', standardLoginOtpId);
    }
    if (otp != null && otp !== '') {
      parameters.set('otp', otp);
    }
    const query = parameters.toString();
    //if url is not empty, then add the query to the url
    const url =
      query.length > 0
        ? `${apiURL}/auth/login/otp/verify?${query}`
        : `${apiURL}/auth/login/otp/verify`;
    return this.executeWithKeycloakRefresh<VerifyOtpResponse>((accessToken) =>
      axios.put(
        url,
        {},
        {
          headers: {
            ...getRoutePathHeader(),
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            loginUserId: loginUserId ?? undefined,
          },
          withCredentials: true,
        }
      )
    );
  }
  public async getTermsCondition() {
    const loginUserId = getLoginUserId();
    const isPreTermsConditionFlow = isPartialAuthActive();
    return this.executeWithKeycloakRefresh(
      (accessToken) =>
        axios.get(`${apiURL}/auth/terms-condition`, {
          headers: {
            ...getRoutePathHeader(),
            ...(isPreTermsConditionFlow
              ? {
                  Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
                  loginUserId: loginUserId ?? undefined,
                }
              : {}),
          },
          withCredentials: true,
        }),
      { refreshMode: isPreTermsConditionFlow ? 'beforeAcceptCondition' : 'afterAcceptCondition' }
    );
  }
  public async acceptTermsCondition(isAccept: boolean) {
    const loginUserId = getLoginUserId();
    return this.executeWithKeycloakRefresh((accessToken) => {
      const currentRefreshToken = getRefreshToken();
      return axios.post(
        `${apiURL}/auth/terms-condition?isAccept=${isAccept}`,
        {},
        {
          headers: {
            ...getRoutePathHeader(),
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            refreshToken: currentRefreshToken ? `Bearer ${currentRefreshToken}` : undefined,
            loginUserId: loginUserId ?? undefined,
          },
          withCredentials: true,
        }
      );
    });
  }
  public getUserInfo(): Promise<UserInfo> {
    const credentials = this.cookies.get(isLoggedKey) as boolean;
    if (!credentials) {
      return Promise.reject(new Error('No access token found'));
    }
    const userData = JSON.parse(localStorage.getItem('userData') as string);
    return Promise.resolve(userData);
  }
  public async logout(isCompleteLogout: boolean) {
    try {
      const response = await HttpClient.post(
        `${apiURL}/auth/logout?isCompleteLogout=${isCompleteLogout}`,
        {},
        {
          headers: getRoutePathHeader(),
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      logger.error('Login error:', error);
      void this.logAuthApiError(error, 'LOGOUT');
    }
  }

  public customLogout() {
    try {
      const pendingRedirectUrl = localStorage.getItem('pendingRedirectUrl');
      // Compare tour "seen" flags are meant to persist forever per user, across logout/login —
      // not just for the current browser session — so snapshot and restore them around the clear.
      const compareTourSeenEntries: Array<[string, string]> = [];
      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index);
        if (key?.startsWith(COMPARE_TOUR_SEEN_STORAGE_PREFIX)) {
          const value = localStorage.getItem(key);
          if (value !== null) {
            compareTourSeenEntries.push([key, value]);
          }
        }
      }

      localStorage.clear();
      if (pendingRedirectUrl) {
        localStorage.setItem('pendingRedirectUrl', pendingRedirectUrl);
      }
      for (const [key, value] of compareTourSeenEntries) {
        localStorage.setItem(key, value);
      }
      clearAllCookies();
      sessionStorage.clear();
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  }
  public async refreshToken(): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await axios.post(
        `${apiURL}/auth/token/refresh`,
        {},
        {
          headers: getRoutePathHeader(),
        }
      );
      return response.data;
    } catch (error) {
      logger.error('Login error:', error);
      void this.logAuthApiError(error, 'REFRESH_TOKEN_API');
      throw error;
    }
  }

  public isAuthenticated(): boolean {
    const credentials = this.cookies.get(isLoggedKey) as boolean;
    return credentials;
  }
  public async sendResetPasswordEmail(
    email: string,
    captcha?: { token: string; action: string }
  ): Promise<ForgotPasswordResponse> {
    const accessToken = getAccessToken();
    try {
      const payload: { email: string; action?: string; token?: string } = {
        email,
      };
      if (captcha) {
        payload.action = captcha.action;
        payload.token = captcha.token;
      }

      const response: AxiosResponse<ForgotPasswordResponse> = await axios.post(
        `${apiURL}/public/forgot-password/link`,
        payload,
        {
          headers: {
            ...getRoutePathHeader(),
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      logger.error('Login error:', error);
      void this.logAuthApiError(error, 'SEND_RESET_PASSWORD_EMAIL');
      throw error;
    }
  }

  public async validateResetToken(token: string) {
    try {
      const response = await axios.get<ValidateForgotPasswordLinkResponse>(
        `${apiURL}/public/forgot-password/link/valid?forgotPasswordLink=${token}`,
        {
          headers: getRoutePathHeader(),
        }
      );
      return response.data;
    } catch (error) {
      void this.logAuthApiError(error, 'VALIDATE_RESET_TOKEN');
      throw error;
    }
  }
  public async resetPassword(payload: ResetPasswordPayload) {
    try {
      const response = await axios.put<ResetPasswordResponse>(
        `${apiURL}/api/v1/public/forgot-password`, payload,
        {
          headers: getRoutePathHeader(),
        }
      );
      return response.data;
    } catch (error) {
      void this.logAuthApiError(error, 'RESET_PASSWORD');
      throw error;
    }
  }

  public async getVendorUsers() {
    const loginUserId = getLoginUserId();
    const isPreTermsConditionFlow = isPartialAuthActive();
    return this.executeWithKeycloakRefresh((accessToken) =>
      axios.get(`${apiURL}/auth/vendor-users`, {
        headers: {
          ...getRoutePathHeader(),
          ...(isPreTermsConditionFlow
            ? {
              Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
              loginUserId: loginUserId ?? undefined,
            }
            : {}),
        },
        withCredentials: true,
      }),
      { refreshMode: isPreTermsConditionFlow ? 'beforeAcceptCondition' : 'afterAcceptCondition' }
    );
  }

  public async getRenderingStyleResourceDocs() {
    const loginUserId = getLoginUserId();
    const isPreTermsConditionFlow = isPartialAuthActive();
    return this.executeWithKeycloakRefresh((accessToken) =>
      axios.get(`${apiURL}/auth/rendering-style-resource-docs`, {
        headers: {
          ...getRoutePathHeader(),
          ...(isPreTermsConditionFlow
            ? {
              Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
              loginUserId: loginUserId ?? undefined,
            }
            : {}),
        },
        withCredentials: true,
      }),
      { refreshMode: isPreTermsConditionFlow ? 'beforeAcceptCondition' : 'afterAcceptCondition' }
    );
  }

  public async getMtobResourceDocs() {
    const loginUserId = getLoginUserId();
    const isPreTermsConditionFlow = isPartialAuthActive();
    return this.executeWithKeycloakRefresh((accessToken) =>
      axios.get(`${apiURL}/auth/mtob-resource-docs`, {
        headers: {
          ...getRoutePathHeader(),
          ...(isPreTermsConditionFlow
            ? {
              Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
              loginUserId: loginUserId ?? undefined,
            }
            : {}),
        },
        withCredentials: true,
      }),
      { refreshMode: isPreTermsConditionFlow ? 'beforeAcceptCondition' : 'afterAcceptCondition' }
    );
  }

  public async getAuthViewDocument(payload: AuthViewDocumentPayload): Promise<AxiosResponse<Blob> | GetSharedData> {
    const isPreTermsConditionFlow = isPartialAuthActive();
    const parameters = {
      ...(payload.json ? { json: true } : {}),
      filename: payload.filename,
      path: payload.path,
      type: payload.type,
      compare: payload.compare,
      location: payload.location,
      prodID: payload.prodID,
      isMtob: payload.isMtob,
      ...(payload.erenStyleId ? { erenStyleId: payload.erenStyleId } : (payload.renStyleID == null ? {} : { renStyleID: payload.renStyleID })),
    };

    if (payload.json) {
      const response = await this.executeWithKeycloakRefresh((accessToken) =>
        axios.get<GetSharedRedirectResponse>(`${apiURL}/auth/view-doc`, {
          headers: isPreTermsConditionFlow
            ? {
              Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            }
            : undefined,
          params: parameters,
          withCredentials: true,
        }),
      { refreshMode: isPreTermsConditionFlow ? 'beforeAcceptCondition' : 'afterAcceptCondition' }
      );
      return response.data;
    }

    return this.executeWithKeycloakRefresh<AxiosResponse<Blob>>(async (accessToken) => {
      const response = await axios.get<Blob>(`${apiURL}/auth/view-doc`, {
        responseType: 'blob',
        headers: isPreTermsConditionFlow
          ? {
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          }
          : undefined,
        params: parameters,
        withCredentials: true,
      });

      return {
        ...response,
        data: response,
      };
    }, { refreshMode: isPreTermsConditionFlow ? 'beforeAcceptCondition' : 'afterAcceptCondition' }
    );
  }

  /** Storage download — auth path pre-T&C (Bearer), normal path after T&C accepted. */
  public async getAuthStorageDownload(storagePath: string): Promise<Blob> {
    const isPreTermsConditionFlow = isPartialAuthActive();
    const downloadUrl = isPreTermsConditionFlow
      ? `${apiURL}/auth/storage/download/${storagePath}`
      : `${apiURL}/storage/download/${storagePath}`;
    return this.executeWithKeycloakRefresh<Blob>((accessToken) =>
      axios.get<Blob>(downloadUrl, {
        responseType: 'blob',
        headers: isPreTermsConditionFlow
          ? {
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          }
          : undefined,
        withCredentials: true,
      }),
    { refreshMode: isPreTermsConditionFlow ? 'beforeAcceptCondition' : 'afterAcceptCondition' }
    );
  }
}

const authService = new AuthService();
export default authService;
